import type { Severity, Warning } from '../lib/warnings';

const LABEL: Record<Severity, string> = { error: 'error', warning: 'warning', info: 'note' };

interface Props {
  warnings: Warning[];
}

export default function WarningsPanel({ warnings }: Props) {
  const counts = warnings.reduce<Record<string, number>>((acc, w) => {
    acc[w.severity] = (acc[w.severity] ?? 0) + 1;
    return acc;
  }, {});
  const summary = (['error', 'warning', 'info'] as const)
    .filter((s) => counts[s])
    .map((s) => `${counts[s]} ${LABEL[s]}${counts[s] === 1 ? '' : 's'}`)
    .join(' · ');

  return (
    <section className="panel" aria-labelledby="findings-heading" data-testid="warnings">
      <header>
        <h2 id="findings-heading">Findings</h2>
        <span className="panel-note" data-testid="warnings-summary">
          {summary || 'nothing to flag'}
        </span>
      </header>
      {warnings.length === 0 ? (
        <p className="all-clear">
          No misconfigurations detected. Window boundaries divide evenly, retention grows with each
          hop, the schedule leaves room for late writes, and the aggregates cascade cleanly.
        </p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {warnings.map((w) => (
            <li className={`finding ${w.severity}`} key={w.id} data-testid={`finding-${w.id}`}>
              <span className="badge">{LABEL[w.severity]}</span>
              <div>
                <h3>{w.title}</h3>
                <p>
                  {w.detail}{' '}
                  <a href={w.reference.url} target="_blank" rel="noreferrer noopener">
                    {w.reference.title}
                  </a>
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
